import { Box, Button, Text, Spinner } from "theme-ui";
import Icon from "./common/Icon";
import { useRef, useState, useEffect } from "react";
import CustomCheckbox from "./common/Checkbox";
import SearchField from "./SearchSelect";
import apiInvocation from "../api/apiInvocation";
import { ImageRequest } from "../types/imagerequest";

interface UploadAreaProps {
  responseGenerationStarted: () => void;
  responseGenerated: (result: ImageRequest) => void;
}

const UploadArea = ({
  responseGenerationStarted,
  responseGenerated,
}: UploadAreaProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [isIncludingCoordinates, setIsIncludingCoordinates] = useState(false);
  const [coordinates, setCoordinates] = useState<{
    latitude: number | null;
    longitude: number | null;
  }>({ latitude: null, longitude: null });
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [validationMessage, setValidationMessage] = useState<string>("");
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [siteNames, setSiteNames] = useState<string[]>([]);

  useEffect(() => {
    apiInvocation.getAllSites()
      .then((sites) => setSiteNames(sites.map((s) => s.name)))
      .catch((err) => console.error("Error loading sites:", err));
  }, []);

  const handleUploadButtonClick = () => {
    if (!selectedLocation && (!coordinates.latitude || !coordinates.longitude)) {
      setValidationMessage(
        "Please select a location from the list or use coordinates from geolocation."
      );
      return;
    }
    fileInputRef.current?.click();
  };

  const handleTakePhotoClick = () => {
    if (!selectedLocation && (!coordinates.latitude || !coordinates.longitude)) {
      setValidationMessage(
        "Please select a location from the list or use coordinates from geolocation."
      );
      return;
    }
    cameraInputRef.current?.click();
  };

  const handleIncludingCoordinatesChange = () => {
    setValidationMessage(""); // Clear previous validation message
    if (!isIncludingCoordinates && !isFetchingLocation) {
      if (navigator.geolocation) {
        setIsFetchingLocation(true); // Start loading
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setCoordinates({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
            setIsFetchingLocation(false); // Stop loading
            setIsIncludingCoordinates(true);
          },
          () => {
            setCoordinates({ latitude: null, longitude: null });
            setIsFetchingLocation(false); // Stop loading
            setIsIncludingCoordinates(false);
          }
        );
      }
    } else {
      setIsIncludingCoordinates(false);
      setCoordinates({ latitude: null, longitude: null });
    }
  };

  const fetchResults = async (query: string): Promise<string[]> => {
    return siteNames.filter((item) =>
      item.toLowerCase().includes(query.toLowerCase())
    );
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    responseGenerationStarted();
    const file = event.target.files?.[0];
    if (file) {
      try {
        const response = await apiInvocation.uploadPhoto(
          file,
          coordinates.longitude || 0,
          coordinates.latitude || 0,
          true,
          selectedLocation
        );
        responseGenerated(response);
      } catch (error) {
        console.error("Error uploading photo:", error);
      }
    }
  };

  return (
    <Box
      variant="cards.primary"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        mt: [4, 6],
        py: [10, 12],
        px: 8,
      }}
    >
      <Button
        variant="primary"
        onClick={handleUploadButtonClick}
        disabled={!selectedLocation && (!coordinates.latitude || !coordinates.longitude)}
        sx={{
          mb: [2, 4],
          pr: [8, 9],
          pl: [7, 8],
          display: "flex",
          alignItems: "center",
          gap: 4,
          opacity:
            !selectedLocation && (!coordinates.latitude || !coordinates.longitude)
              ? 0.5
              : 1,
          cursor:
            !selectedLocation && (!coordinates.latitude || !coordinates.longitude)
              ? "not-allowed"
              : "pointer",
        }}
      >
        <Icon name="progress-upload" size={20} color="buttonText" />
        Upload Image
      </Button>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
        accept=".png, .jpg, .jpeg, .webp"
      />

      <input
        type="file"
        ref={cameraInputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
        accept=".png, .jpg, .jpeg, .webp"
        capture="environment"
      />

      <Box
        sx={{
          display: "flex",
          gap: [1, 2],
          alignItems: "center",
          justifyContent: "center",
          fontSize: [1, 4],
          fontWeight: "medium",
          textAlign: "center",
          mb: [6, 9],
        }}
      >
        <Text sx={{ color: "secondaryText" }}>or </Text>
        <Text
          onClick={handleTakePhotoClick}
          sx={{
            color: "primaryText",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          Take a photo
        </Text>
        <Icon
          name="camera"
          size={[12, 16]}
          color="primaryText"
          sx={{ ml: 1 }}
        />
      </Box>

      <Text sx={{ fontSize: [1, 3], color: "secondaryText", mb: [7, 10] }}>
        (up to resolution 5,000 x 5,000 px)
      </Text>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: [7, 9] }}>
        {isFetchingLocation ? (
          <Spinner sx={{ ml: 2 }} size={20} />
        ) : (
          <CustomCheckbox
            checked={isIncludingCoordinates}
            onChange={handleIncludingCoordinatesChange}
            sx={{ cursor: isFetchingLocation ? "not-allowed" : "pointer" }}
          />
        )}
        <Text sx={{ color: "primaryText", fontWeight: "medium", fontSize: [1, 4] }}>
          Include coordinates
        </Text>
      </Box>



      <SearchField
        fetchResults={fetchResults}
        onSelect={(location) => {
          setValidationMessage("");
          setSelectedLocation(location);
        }}
        placeholderMessage="Enter location"
      />

      {validationMessage && (
        <Text
          sx={{
            color: "red",
            fontSize: [1, 3],
            mt: 3,
            textAlign: "center",
          }}
        >
          {validationMessage}
        </Text>
      )}
    </Box>
  );
};

export default UploadArea;